import { rechargeOptions } from "constants/subscriptions";

import React from "react";

import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import BackdropLoading from "components/elements/BackdropLoading";
import CenterWrapper from "components/elements/CenterWrapper";
import { Credit } from "components/icons";
import SubscriptionBadge from "components/modules/SubscriptionBadge";
import InvoiceSetup from "components/pages/Settings/InvoiceSetup";
import { fontWeight } from "config/Theme/options";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { Trans, useTranslation } from "react-i18next";
import { RechargeState, SubscriptionType } from "types/enums";
import { renderDate, renderNumber } from "utils/formatter";

import { useRechargeCard } from "./module/useRechargeCard";
import { StyledBox } from "./styles";

const StatusBadge = styled(Box)<{ active: boolean }>(({ active }) => {
  const { colors } = useMUIThemeModeContext();
  const color = active ? colors.success : colors.darkBlue;

  return {
    backgroundColor: alpha(color, 0.08),
    color: color,
    borderRadius: "10px",
    padding: "2px 8px",
    fontWeight: fontWeight.medium,
    fontSize: "1rem",
    width: "fit-content",
  };
});

const Card: React.FC<typeof rechargeOptions[0]> = ({ type, prevType, defaultPrice, unlocks, discountFrom }) => {
  const { colors } = useMUIThemeModeContext();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    isOpenBackdropLoading,
    data,
    responseStatus,
    creditsPaymentTypesData,
    priceWithoutTax,
    creditsAmount,
    isActive,
    price,
    status,
    subscriptionType,
    rechargeType,
    handleCancel,
    handleActivate,
    invoiceSetupOpen,
    setInvoiceSetupOpen,
    handleRefreshInvoiceSetup,
    userInvoiceData,
  } = useRechargeCard(defaultPrice, type, prevType);

  if (!data && responseStatus !== 204) {
    return (
      <StyledBox>
        <CenterWrapper>
          <CircularProgress color="primary" />
        </CenterWrapper>
      </StyledBox>
    );
  }

  const getStatusSubtitle = (status?: RechargeState) => {
    if (i18n.exists(`recharge.${status}.subtitle`)) {
      return (
        <Typography pt={1} fontSize={{ xs: "0.75rem", md: "0.875rem" }} color={colors.gray400} fontWeight={fontWeight.regular}>
          {t(`recharge.${status}.subtitle`)}
        </Typography>
      );
    }
  };

  const getPriceWithTax = () => {
    if (discountFrom) {
      const discountFromPaymentType = creditsPaymentTypesData?.creditsPaymentTypes?.[discountFrom];
      const priceFrom = discountFromPaymentType?.czkAmountWithTax ?? defaultPrice;

      return (
        <Stack direction={"row"} spacing={0.5} alignItems="center">
          <Typography
            fontWeight={fontWeight.regular}
            fontSize={{ xs: "1rem", md: "1.25rem" }}
            color={colors.gray600}
            sx={{ textDecoration: "line-through" }}>
            {`${renderNumber(priceFrom, 2, !Number.isInteger(priceFrom))}\u00a0${t("currencySymbol.CZK")}`}
          </Typography>
          <Typography fontWeight={fontWeight.bold} fontSize={{ xs: "1.25rem", md: "1.5rem" }} color={"primary"}>
            {`${renderNumber(price, 2, !Number.isInteger(price))}\u00a0${t("currencySymbol.CZK")}`}
          </Typography>
        </Stack>
      );
    }

    return (
      <Typography fontWeight={fontWeight.bold} fontSize={{ xs: "1.25rem", md: "1.5rem" }} color={colors.gray900}>
        {`${renderNumber(price, 2, !Number.isInteger(price))}\u00a0${t("currencySymbol.CZK")}`}
      </Typography>
    );
  };

  const getPriceWithoutTax = () => {
    if (!priceWithoutTax || priceWithoutTax === price) return null;

    if (discountFrom) {
      const discountFromPaymentType = creditsPaymentTypesData?.creditsPaymentTypes?.[discountFrom];
      const priceFromWithoutTax = discountFromPaymentType?.czkAmountWithoutTax;

      return (
        <Stack direction={"row"} spacing={0.5} alignItems="center">
          {priceFromWithoutTax && (
            <Typography
              fontSize={{ xs: "0.75rem", md: "0.875rem" }}
              color={colors.gray400}
              fontWeight={fontWeight.regular}
              sx={{ textDecoration: "line-through" }}>
              {`${renderNumber(priceFromWithoutTax, 2, !Number.isInteger(priceFromWithoutTax))}\u00a0${t(
                "currencySymbol.CZK"
              )}`}
            </Typography>
          )}
          <Typography
            fontSize={{ xs: "0.75rem", md: "0.875rem" }}
            color={colors.gray400}
            fontWeight={fontWeight.regular}>
            {t("recharge.withoutTax", {
              price: renderNumber(priceWithoutTax, 2, !Number.isInteger(priceWithoutTax)),
            })}
          </Typography>
        </Stack>
      );
    }

    return (
      <Typography fontSize={{ xs: "0.75rem", md: "0.875rem" }} color={colors.gray400} fontWeight={fontWeight.regular}>
        {t("recharge.withoutTax", {
          price: renderNumber(priceWithoutTax, 2, !Number.isInteger(priceWithoutTax)),
        })}
      </Typography>
    );
  };

  const getActivateButton = () => {
    if (!userInvoiceData?.invoiceData) {
      return (
        <Button fullWidth variant="contained" onClick={() => setInvoiceSetupOpen(true)}>
          {t("recharge.setInvoice")}
        </Button>
      );
    }

    if (isActive) {
      return (
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          sx={{ color: colors.gray900, borderColor: colors.gray300 }}
          onClick={() => handleCancel()}>
          {t("recharge.deactivate")}
        </Button>
      );
    }

    return (
      <Button fullWidth variant="contained" onClick={handleActivate}>
        {subscriptionType !== unlocks ? t("recharge.activateWith", { subscription: unlocks }) : t("recharge.activate")}
      </Button>
    );
  };

  return (
    <StyledBox>
      <Stack spacing={2}>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <SubscriptionBadge subscription={unlocks} disable size={isMobile ? "small" : "normal"} />
          <Stack spacing={0.5} direction="row" alignItems="center">
            <Stack spacing={0.5} direction="row" alignItems="center">
              <Typography
                fontWeight={fontWeight.bold}
                fontSize={{ xs: "1.25rem", md: "1.5rem" }}
                color={colors.gray900}>
                {`+${creditsAmount}`}
              </Typography>
              <Credit sx={{ fontSize: 24 }} />
            </Stack>
            <Typography
              fontWeight={fontWeight.regular}
              fontSize={{ xs: "0.75rem", md: "0.875rem" }}
              color={colors.gray400}>
              {`/ ${t("datetime.month")} `}
            </Typography>
          </Stack>
        </Stack>
        <Box>
          <Stack spacing={0.5} direction="row" alignItems="center">
            {getPriceWithTax()}
            <Typography
              fontWeight={fontWeight.regular}
              fontSize={{ xs: "0.75rem", md: "0.875rem" }}
              color={colors.gray400}>
              {`/ ${t("datetime.month")} `}
            </Typography>
          </Stack>
          {getPriceWithoutTax()}
        </Box>
        {data && data.type === rechargeType && (
          <Stack spacing={0.5}>
            <StatusBadge active={isActive}>
              {t(`recharge.${status}.badge`, { date: renderDate(data.createdAt) })}
            </StatusBadge>
            {getStatusSubtitle(status)}
          </Stack>
        )}
      </Stack>
      <Box pt={2}>{getActivateButton()}</Box>
      <BackdropLoading open={isOpenBackdropLoading} />
      {invoiceSetupOpen && (
        <InvoiceSetup onClose={() => setInvoiceSetupOpen(false)} onSuccess={handleRefreshInvoiceSetup} />
      )}
    </StyledBox>
  );
};

export default Card;
